import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * SecuritySettings component with SafeLine theme matching verification pages
 */
const SecuritySettings = () => {
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  const [settings, setSettings] = useState({
    twoFactorEnabled: false,
    requirePasswordChange: true,
    passwordExpiryDays: 90,
    maxLoginAttempts: 5,
    lockoutDuration: 30,
    enableAuditLog: true,
    sessionTimeout: 30,
    requireStrongPassword: true,
    minPasswordLength: 8
  });

  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { user } = useAuth();
  const [loadingInitial, setLoadingInitial] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoadingInitial(true);
        const userId = user?.id;
        
        if (userId) {
          const userSettings = await apiService.settings.getByCategory(userId, 'security');
          if (userSettings && Object.keys(userSettings).length > 0) {
            setSettings(userSettings);
            console.log('Loaded security settings:', userSettings);
          }
        }
      } catch (error) {
        console.error('Error loading security settings:', error);
        setSnackbar({
          open: true,
          message: 'Error loading settings. Using defaults.',
          severity: 'warning'
        });
      } finally {
        setLoadingInitial(false);
      }
    };

    loadSettings();
  }, [user]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    if (!user?.id) {
      setSnackbar({
        open: true,
        message: 'Error: User not authenticated',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      await apiService.settings.save(user.id, 'security', settings);
      setSnackbar({
        open: true,
        message: 'Security settings saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving security settings:', error);
      setSnackbar({
        open: true,
        message: 'Error saving settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSettings({
      twoFactorEnabled: false,
      requirePasswordChange: true,
      passwordExpiryDays: 90,
      maxLoginAttempts: 5,
      lockoutDuration: 30,
      enableAuditLog: true,
      sessionTimeout: 30,
      requireStrongPassword: true,
      minPasswordLength: 8
    });
    setPasswords({ current: '', new: '', confirm: '' });
    setSnackbar({ open: true, message: 'Settings reset to defaults', severity: 'info' });
  };

  const settingsSteps = [
    'Authentication',
    'Password Policy',
    'Session Management',
    'Audit & Monitoring'
  ];

  return (
    <Box sx={{}}>
      <Grid container spacing={2}>
        {/* Settings Process Guide */}
        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ color: safeLineColors.flameOrange, mr: 1.5 }} />
                  <Typography variant="h6" sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'bold' }}>
                    Security Configuration
                  </Typography>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            <CardContent>
              <Stepper orientation="vertical">
                {settingsSteps.map((step, index) => (
                  <Step key={step} active={true}>
                    <StepLabel>
                      <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                        {step}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                        {index === 0 && 'Configure two-factor authentication and login security'}
                        {index === 1 && 'Set password requirements and expiration policies'}
                        {index === 2 && 'Manage session timeouts and access controls'}
                        {index === 3 && 'Enable audit logging and security monitoring'}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Form */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2,
                      backgroundColor: safeLineColors.flameOrange,
                      mr: 2
                    }}>
                      <SecurityIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        color: safeLineColors.darkCharcoal 
                      }}>
                        Security Settings
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal 
                      }}>
                        Configure authentication and security policies
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            
            <CardContent sx={{ p: 3 }}>
              {/* Authentication Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <LockIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Authentication Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.twoFactorEnabled}
                            onChange={(e) => handleSettingChange('twoFactorEnabled', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Two-Factor Authentication
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.requirePasswordChange}
                            onChange={(e) => handleSettingChange('requirePasswordChange', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Require Password Change
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.requireStrongPassword}
                            onChange={(e) => handleSettingChange('requireStrongPassword', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Require Strong Password
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password Expiry (days)"
                      type="number"
                      value={settings.passwordExpiryDays}
                      onChange={(e) => handleSettingChange('passwordExpiryDays', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Minimum Password Length"
                      type="number"
                      value={settings.minPasswordLength}
                      onChange={(e) => handleSettingChange('minPasswordLength', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{ mt: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Login Security */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Login Security
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Max Login Attempts"
                      type="number"
                      value={settings.maxLoginAttempts}
                      onChange={(e) => handleSettingChange('maxLoginAttempts', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Lockout Duration (minutes)"
                      type="number"
                      value={settings.lockoutDuration}
                      onChange={(e) => handleSettingChange('lockoutDuration', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Session Management */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Session Management
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Session Timeout (minutes)"
                      type="number"
                      value={settings.sessionTimeout}
                      onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableAuditLog}
                          onChange={(e) => handleSettingChange('enableAuditLog', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                          Enable Audit Logging
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  sx={{
                    borderColor: safeLineColors.flameOrange,
                    color: safeLineColors.flameOrange,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      backgroundColor: safeLineColors.orangeTint,
                      borderColor: safeLineColors.brightOrange
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSettings}
                  disabled={loading}
                  sx={{
                    background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SecuritySettings; 